/* assets/showcase.js
   Shared horizontal-scroll "1 / N" counter + prev/next-arrow behaviour for
   every [data-showcase] component on the theme (homepage image showcase,
   homepage product carousels, and the mobile product-gallery swiper).
   Extracted into one guarded script so multiple sections on the same page
   don't double-bind listeners on each other's elements. */
(function () {
  // Last known pointer position, tracked once globally (not per-carousel)
  // for the "hover follows the card under a still cursor" fix inside the
  // autoplay block below — see the long comment there and the matching
  // .is-live-hover rule in theme-minimal.css. Plain CSS :hover only ever
  // re-evaluates on an actual pointer event; it has no way to notice that
  // the DOM has scrolled a different card underneath an UNMOVING cursor.
  // pointermove covers mouse, pen, and touch-drag alike; passive since we
  // only ever read the coordinates, never preventDefault.
  var lastPointerX = null;
  var lastPointerY = null;
  document.addEventListener('pointermove', function (e) {
    lastPointerX = e.clientX;
    lastPointerY = e.clientY;
  }, { passive: true });

  function init() {
    document.querySelectorAll('[data-showcase]').forEach(function (root) {
      if (root.dataset.showcaseBound) return;
      root.dataset.showcaseBound = 'true';

      var track = root.querySelector('[data-showcase-track]');
      var counter = root.querySelector('[data-showcase-counter]');
      if (!track) return;
      var slides = Array.from(track.children);
      var total = slides.length;
      // Fixed forever at the REAL product count, captured here before any
      // clone-padding below can grow `slides`/`total` past it. Used only
      // by updateCounter()'s "N / total" label — see the comment on the
      // duplication block further down for why `slides`/`total` themselves
      // get reassigned (not left as this original snapshot) once cloning
      // happens, and why the label still needs this separate, un-reassigned
      // value to keep reading "N / 4" instead of counting the clones too.
      var originalTotal = total;

      function currentIndex() {
        var trackRect = track.getBoundingClientRect();
        var closest = 0;
        var closestDist = Infinity;
        slides.forEach(function (slide, i) {
          var dist = Math.abs(slide.getBoundingClientRect().left - trackRect.left);
          if (dist < closestDist) { closestDist = dist; closest = i; }
        });
        return closest;
      }

      function updateCounter() {
        // currentIndex() can return an index into the clone-padded `slides`
        // array (see below), which would otherwise print something like
        // "9 / 4" once autoplay pans into cloned territory. % originalTotal
        // wraps it back into the real 1..originalTotal range, treating each
        // clone as visually equivalent to its original counterpart — which
        // is exactly how it reads on screen, since a clone is a full
        // cloneNode(true) of the original tile.
        if (counter) counter.textContent = ((currentIndex() % originalTotal) + 1) + ' / ' + originalTotal;
      }

      track.addEventListener('scroll', function () {
        window.requestAnimationFrame(updateCounter);
      }, { passive: true });

      var prevBtn = root.querySelector('[data-showcase-prev]');
      var nextBtn = root.querySelector('[data-showcase-next]');
      if (prevBtn) prevBtn.addEventListener('click', function () {
        var i = Math.max(0, currentIndex() - 1);
        slides[i].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      });
      if (nextBtn) nextBtn.addEventListener('click', function () {
        var i = Math.min(total - 1, currentIndex() + 1);
        slides[i].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      });

      // Slow continuous auto-pan, opt-in per instance via data-showcase-autoplay
      // (used by the homepage product carousel and the "And what about
      // these?" product-recommendations carousel — not every showcase on
      // the theme). Pans back and forth (no jarring reset-to-start jump).
      //
      // Per explicit request, this now NEVER pauses for any reason — not on
      // click/tap, not on mouse-wheel, not while the cursor is resting over
      // the block/cards. An earlier version paused on pointerdown/wheel/
      // touchstart and resumed after a 2.5s idle delay; in practice that
      // read as "the carousel randomly stops" (any wheel-scroll of the page
      // that happened to pass over the track, or a stray click, silently
      // froze it, and normal continued scrolling/mousing near it kept
      // re-arming the pause so it could sit still indefinitely) — exactly
      // the "no longer moving" symptom reported. Always plays regardless of
      // the OS/browser "reduce motion" setting too, per the same earlier
      // explicit request — previously skipped entirely for
      // prefers-reduced-motion, which silently disabled it for anyone with
      // that accessibility setting on.
      if (root.hasAttribute('data-showcase-autoplay')) {
        var AUTOPLAY_SPEED = 44; // px per second — 55 * 0.8, 20% slower per
                                  // explicit request (55 itself was bumped
                                  // up from an original 26 so the motion
                                  // would read as unmistakable at a glance)
        // 16ms (~60fps) rather than 50ms (~20fps): at 50ms, each tick moved
        // scrollLeft by a few px in one visible jump 20 times a second,
        // which reads as stepping/stuttering rather than smooth motion —
        // especially once browsers round scrollLeft to the nearest whole
        // pixel, which turns "2.75px every tick" into an uneven, visible
        // 3-3-2-3-2 pattern. Quintupling the tick rate makes each individual
        // step small enough (under 1px at this speed) that the rounding is
        // no longer perceptible, without giving up the reliability
        // setInterval has over requestAnimationFrame in this specific
        // preview-iframe context (see the comment below).
        var TICK_MS = 16;
        var direction = 1;

        // Scroll-snap fights a slow programmatic scrollLeft ramp — switched
        // off permanently (via this class, see theme-minimal.css
        // .showcase__track.is-autoplay-active) for the whole lifetime of an
        // autoplay-enabled track, rather than toggled on interaction, now
        // that autoplay itself never stops driving scroll position. Set
        // once, up front, before the interval loop starts. (Previously this
        // was also the fix for a mobile-only "works on web, static on
        // mobile" bug: touch WebKit resolves an active scroll-snap-type
        // against a scrollLeft write far more eagerly than desktop engines,
        // silently snapping every autoplay tick straight back to 0 before
        // it ever painted. Leaving snap disabled unconditionally, rather
        // than only during active autoplay, keeps that fix intact too.)
        track.classList.add('is-autoplay-active');

        // Root cause of "works on the homepage, static on the product
        // page": the homepage product carousel pulls from a whole
        // collection (up to 20 products, sections/product-carousel.liquid's
        // own limit), so its track is always far wider than the viewport
        // and there's plenty of room to auto-pan across. The "And what
        // about these? 👀" recommendations carousel (sections/product-
        // recommendations.liquid) is capped at whatever Shopify's
        // recommendation engine actually returns — often just 4 products —
        // and 4 slides at this track's own width/gap frequently DON'T add
        // up to more than the viewport's width, especially on a wide
        // desktop screen. When that happens track.scrollWidth <=
        // track.clientWidth: there's genuinely zero scrollable distance,
        // so every tick below hits `if (max <= 0) return;` and the track
        // never moves at all — not a bug in the loop itself, just nothing
        // to loop across. Confirmed directly against a supplied screenshot
        // (4 recommended products, comfortably narrower than the page).
        //
        // Fixed generically (not hand-tuned to the recommendations case
        // specifically) by padding out the TRACK's own rendered content
        // with clones of the full original slide sequence, repeated until
        // there's comfortably more than a full viewport's worth of extra
        // width to scroll across.
        //
        // CORRECTION to what an earlier version of this comment claimed:
        // leaving `slides`/`total` pointed at the original, pre-clone
        // snapshot did NOT leave the arrows "completely unaffected" — it
        // broke them. currentIndex() only ever measures distance against
        // the ORIGINAL slide elements captured in `slides`; once autoplay
        // has panned past them into the cloned tail (which, for a 4-slide
        // recommendations row, is most of the track's actual width), every
        // one of those original elements sits far off to one side, so
        // currentIndex() kept returning whatever original slide happened
        // to be marginally closest — usually still index 0 — regardless of
        // where the track had actually scrolled to. Clicking an arrow then
        // computed its target from that wrong index and (via
        // syncPosToSlide, below) forced `pos` back near the start on the
        // very next 16ms autoplay tick, which reads as "the arrows do
        // nothing" — exactly what was reported.
        //
        // Fixed by reassigning `slides`/`total` themselves to the FULL,
        // post-clone child list once cloning finishes. Every function that
        // reads `slides`/`total` (currentIndex, both arrow-click handlers
        // above, syncPosToSlide below) closes over these same outer
        // variables by reference, not by the value at the time each
        // function was defined — so this reassignment is picked up
        // automatically the next time any of them runs, with no need to
        // touch their code. The "N / total" counter is the one place that
        // still needs the ORIGINAL count, not the clone-padded one — see
        // originalTotal, captured further up before any of this runs, and
        // updateCounter()'s use of it.
        //
        // isPadded / periodWidth: SECOND correction, on top of the one
        // above. Once the arrows were fixed, this still visibly "glitched"
        // — reported directly as a short, jerky side-to-side flicker,
        // reproducing only on product pages (i.e. only on THIS, the
        // padded/duplicated carousel — the homepage carousel, which never
        // pads, was never touched by this and was never reported broken).
        // Root cause: the bounce logic further down (direction flips
        // between 1 and -1 once `pos` hits 0 or `max`) was written for the
        // homepage carousel, where a real ~20-product collection gives
        // `max` a huge range — thousands of px — so a reversal happens
        // once every minute or so and reads as a slow, deliberate pan.
        // Here, `max` is only ever as big as the modest amount of clone
        // padding just added above (comfortably more than one viewport,
        // per the loop above — often just a few hundred px on a narrow
        // viewport with a handful of recommended products), so at the same
        // 44px/s speed the SAME bounce logic reverses direction every few
        // seconds instead of every minute — a rapid, visibly janky
        // back-and-forth that reads exactly as "glitched" rather than
        // "auto-scrolling."
        //
        // Fixed by giving this, padded case an entirely different (and
        // better-suited) animation: since the track's content past the
        // original slides is nothing but exact repeats of those same
        // slides, autoplay here can run in ONE direction forever and
        // silently wrap `pos` back by exactly one repeat's width the
        // moment it completes a lap — the content one repeat-width later
        // is pixel-identical to the content at pos 0, so the wrap is
        // completely invisible on screen: a real, continuous, never-
        // reversing loop instead of a short bounce. isPadded gates the
        // interval below onto this branch instead of the original
        // bounce, which is left untouched (still exactly what the
        // homepage carousel uses). periodWidth is measured directly off
        // the actual laid-out DOM (the pixel gap between slides[0] and
        // its own first repeat, slides[originalTotal]) rather than
        // computed from CSS widths/gaps by hand, so it's correct
        // regardless of viewport width, slide count, or gap value. */
        var isPadded = false;
        var periodWidth = 0;
        if (track.scrollWidth <= track.clientWidth) {
          var paddingGuard = 0;
          while (track.scrollWidth <= track.clientWidth * 2 && paddingGuard < 10) {
            slides.forEach(function (slide) {
              track.appendChild(slide.cloneNode(true));
            });
            paddingGuard++;
          }
          slides = Array.from(track.children);
          total = slides.length;
          isPadded = true;
          if (slides.length > originalTotal) {
            periodWidth = slides[originalTotal].getBoundingClientRect().left
              - slides[0].getBoundingClientRect().left;
          }
        }

        // track.scrollTo({left, behavior:'auto'}) instead of directly
        // assigning track.scrollLeft — the more robust, standards-focused
        // way to set scroll position programmatically (some WebKit
        // versions handle direct scrollLeft writes less predictably inside
        // a fast setInterval loop). Position is tracked in this closure's
        // own `pos` variable, never read back from track.scrollLeft inside
        // the loop, so a browser coalescing/clamping a scrollTo() write
        // can't throw the running total off.
        var pos = track.scrollLeft;
        track.style.scrollBehavior = 'auto'; // explicit — makes sure no inherited/default smooth-scroll behavior fights the per-tick writes below

        // No pause listeners of any kind here any more — see the comment
        // above this block. The prev/next arrows (defined earlier in this
        // function, shared with the non-autoplay showcases) still jump the
        // track via scrollIntoView({behavior:'smooth'}) when clicked; since
        // `pos` isn't read from track.scrollLeft on every tick (see above),
        // it would otherwise still hold its pre-click value.
        //
        // This USED to resync `pos` via a 500ms setTimeout after the click
        // — reported back as the arrows barely working/not really sliding.
        // Root cause: the setTimeout was far too slow relative to the
        // interval below, which ticks every 16ms. For the entire ~500ms
        // the resync was waiting on, every one of those ~30 ticks kept
        // calling track.scrollTo({left: pos, behavior:'auto'}) with the
        // STALE pre-click `pos` — an instant, non-smooth jump back to the
        // old position, 60 times a second — which fought and effectively
        // cancelled the click's own smooth-scroll animation almost as soon
        // as it started, so clicking an arrow barely appeared to move the
        // track at all.
        //
        // Fixed by computing the click's destination and writing it into
        // `pos` immediately, synchronously, in the same tick as the click
        // — no delay. The very next 16ms interval tick then reinforces
        // that SAME destination instead of fighting it, which (since an
        // 'auto' scrollTo is instant) snaps the track the rest of the way
        // there almost immediately: exactly the fast, responsive
        // arrow-click slide that was asked for, and autoplay simply
        // continues on from the new position afterward. Uses the same
        // getBoundingClientRect delta math as currentIndex() above, rather
        // than the target slide's own offsetLeft, since offsetLeft is
        // measured against the nearest POSITIONED ancestor (not
        // necessarily this track) and would misalign whenever that ancestor
        // isn't the track itself.
        function syncPosToSlide(targetIndex) {
          var trackRect = track.getBoundingClientRect();
          var slideRect = slides[targetIndex].getBoundingClientRect();
          var target = track.scrollLeft + (slideRect.left - trackRect.left);
          var max = track.scrollWidth - track.clientWidth;
          pos = Math.max(0, Math.min(max, target));
          // In the wraparound (isPadded) case the interval below only ever
          // expects `pos` inside [0, periodWidth) — see its own comment.
          // An arrow click can target a slide anywhere in the clone-padded
          // tail (currentIndex() picks whichever actual DOM element, clone
          // or original, is nearest), which would otherwise hand the
          // interval a `pos` many periods past that range; the interval's
          // own single `pos -= periodWidth` only ever removes ONE period
          // per tick, so without reducing it here first it would take
          // several visibly-wrong ticks (a jump backward, in the wrong
          // direction) to catch back up. The modulo brings it in range
          // immediately, so the click always resumes at the equivalent
          // point of the FIRST lap — indistinguishable on screen from
          // whichever repeat was actually clicked, since every repeat is a
          // pixel-identical clone.
          if (isPadded && periodWidth > 0) {
            pos = ((pos % periodWidth) + periodWidth) % periodWidth;
          }
        }
        if (prevBtn) prevBtn.addEventListener('click', function () {
          syncPosToSlide(Math.max(0, currentIndex() - 1));
        });
        if (nextBtn) nextBtn.addEventListener('click', function () {
          syncPosToSlide(Math.min(total - 1, currentIndex() + 1));
        });

        // Per explicit request, the hover-swap effect (front photo ->
        // second product photo, .product-showcase__slide:hover in
        // theme-minimal.css) needs to keep working while this track is
        // auto-panning. Plain CSS :hover can't do that on its own: it only
        // re-evaluates on a real pointer event, so once the mouse itself
        // stops moving, whichever card was last under it stays "hovered"
        // even as autoplay slides a completely different card underneath
        // the still cursor. This re-checks, every tick, which slide is
        // ACTUALLY under the last known pointer position (tracked globally
        // above) and toggles a matching .is-live-hover class — see that
        // class's own rule in theme-minimal.css, added alongside :hover
        // rather than replacing it, so the effect still also fires
        // normally from real mouse movement / on non-autoplay showcases.
        var liveHoverSlide = null;
        function updateLiveHover() {
          if (lastPointerX === null) return;
          var el = document.elementFromPoint(lastPointerX, lastPointerY);
          var slide = el && el.closest ? el.closest('.product-showcase__slide') : null;
          if (slide === liveHoverSlide) return;
          if (liveHoverSlide) liveHoverSlide.classList.remove('is-live-hover');
          if (slide) slide.classList.add('is-live-hover');
          liveHoverSlide = slide;
        }

        // setInterval rather than requestAnimationFrame: rAF callbacks are
        // throttled or fully suspended by some browsers whenever the tab —
        // or, more relevant here, the iframe a page is rendered in — is
        // treated as "not visible" or not the active frame, which is
        // exactly the situation for Shopify's theme-editor preview pane.
        // That would silently stop this loop with no error to catch
        // anywhere. setInterval keeps ticking in that same context, so
        // it's the more reliable driver for an element that's mostly
        // going to be viewed inside that preview iframe.
        setInterval(function () {
          var max = track.scrollWidth - track.clientWidth;
          if (max <= 0) return;
          if (isPadded && periodWidth > 0) {
            // Seamless one-direction loop — see the long comment on
            // isPadded/periodWidth above for why this branch exists
            // instead of reusing the bounce below. `pos` only ever climbs;
            // once it completes exactly one repeat's width, it's wrapped
            // straight back by that same width. Since the track past the
            // originals is nothing but exact repeats of them, the pixels
            // on screen the instant before and the instant after that
            // wrap are identical — there's no reset-to-start jump to see,
            // just continuous forward motion, and never a reversal.
            pos += AUTOPLAY_SPEED * (TICK_MS / 1000);
            if (pos >= periodWidth) pos -= periodWidth;
          } else {
            // Original bounce — unchanged, still what the homepage
            // carousel (which never pads; real product counts already
            // give it a wide `max`) uses.
            pos += direction * AUTOPLAY_SPEED * (TICK_MS / 1000);
            if (pos >= max) {
              pos = max;
              direction = -1;
            } else if (pos <= 0) {
              pos = 0;
              direction = 1;
            }
          }
          track.scrollTo({ left: Math.min(pos, max), behavior: 'auto' });
          updateLiveHover();
        }, TICK_MS);
      }
    });

    // Decorative swimming-fish overlay, shared by the product-page gallery
    // and the homepage split-banner images — any element carrying
    // data-gallery-fish. Sizes --fish-size from the SMALLER of the
    // track's own PARENT element's rendered width/height (not a
    // hardcoded selector like .product-gallery-wrap), so the same code
    // works wherever a .gallery-fish-track is placed: it's always a
    // direct child of whatever box it should visually fill (the product
    // gallery wrap, or a single split-banner tile).
    document.querySelectorAll('[data-gallery-fish]').forEach(function (track) {
      if (track.dataset.fishBound) return;
      track.dataset.fishBound = 'true';

      var referenceEl = track.parentElement;
      if (!referenceEl) return;

      function sizeFish() {
        var rect = referenceEl.getBoundingClientRect();
        // min(width, height) rather than height alone: on the homepage
        // split-banner, height is the merchant's own fixed section-height
        // setting and is reliably the smaller of the two, so this is a
        // no-op there — same size as before. On the product page,
        // .product-gallery-wrap's height is no longer one fixed row, it's
        // the sum of however many images are stacked in the gallery,
        // which grows without bound as more photos are added — sizing
        // straight off that height scaled the fish up right along with
        // it, producing a comically oversized, "distorted"-looking
        // overlay on any product with more than a couple of images.
        // Width stays bounded (it's just the gallery column's own
        // rendered width, unaffected by image count), so using whichever
        // of the two is smaller keeps the fish a sane, stable size
        // regardless of how tall the stack gets.
        var basis = Math.min(rect.width, rect.height);
        if (basis > 0) {
          // basis/15, then *1.32 — a further 20% bigger per explicit
          // request on top of the earlier 10% bump (1.1 * 1.2 = 1.32),
          // still expressed as a fraction of the container's own size so
          // it stays proportionally consistent across breakpoints rather
          // than becoming a fixed px bump.
          track.style.setProperty('--fish-size', ((basis / 15) * 1.32) + 'px');
        }
      }

      sizeFish();

      var referenceImg = referenceEl.querySelector('img');
      if (referenceImg && !referenceImg.complete) {
        referenceImg.addEventListener('load', sizeFish, { once: true });
      }

      var resizeTimer = null;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(sizeFish, 150);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The Shopify theme editor (customizer preview) re-renders a section's
  // HTML via AJAX whenever its settings change, WITHOUT firing a full page
  // load — so a [data-showcase] element edited (or just re-saved) in the
  // editor gets swapped for a brand-new, unbound DOM node that never picks
  // up autoplay/counter/arrow behaviour from the original DOMContentLoaded
  // run above. Re-running init() on Shopify's documented section-load event
  // re-binds anything new (the root.dataset.showcaseBound guard above
  // means already-bound elements are skipped, not double-bound).
  document.addEventListener('shopify:section:load', init);

  // sections/product-recommendations.liquid renders its carousel INSIDE
  // Debut's async product-recommendations placeholder: theme.js fetches
  // the recommended products after page load and swaps them into the
  // page with a plain DOM insert, not Shopify's shopify:section:load
  // event above (that one only ever fires inside the theme editor) — so
  // without this, that specific carousel would render but never bind
  // (no autoplay, no counter, no arrows). Rather than special-case that
  // one AJAX call, a MutationObserver on the whole page catches ANY
  // element added after the initial run — that recommendations swap,
  // or anything similar added later — and re-runs init() for it. init()
  // is cheap to call repeatedly: every already-bound element is skipped
  // instantly via its own dataset guard (showcaseBound / fishBound), so
  // this only does real work for genuinely new elements.
  if ('MutationObserver' in window) {
    var pendingInit = null;
    var observer = new MutationObserver(function () {
      if (pendingInit) return;
      pendingInit = setTimeout(function () {
        pendingInit = null;
        init();
      }, 50);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
